/**
 * gs-cart-drawer.js
 * GripSocks custom sliding cart drawer.
 * Supersedes Impulse's native drawer via capture-phase click + submit interception.
 * Ported from SeatShield ss-cart-drawer.js (2026-06-07)
 */
(function () {
  'use strict';

  const SECTION_ID = 'gs-cart-drawer';
  const POPUP_SECTION_ID = 'gs-checkout-popup';
  const CONTENTS_ID = 'GsCartDrawerContents';
  const DRAWER_ID = 'GsCartDrawer';
  const OVERLAY_ID = 'GsCartOverlay';
  const ANNOUNCE_ID = 'GsCartAnnouncement';

  /* ------------------------------------------------------------------ */
  /* DOM refs (resolved lazily once drawer is in DOM)                     */
  /* ------------------------------------------------------------------ */
  function drawer() { return document.getElementById(DRAWER_ID); }
  function contents() { return document.getElementById(CONTENTS_ID); }
  function overlay() { return document.getElementById(OVERLAY_ID); }
  function announce() { return document.getElementById(ANNOUNCE_ID); }

  /* ------------------------------------------------------------------ */
  /* Focus trap (accessibility — keyboard users can't tab out of drawer) */
  /* ------------------------------------------------------------------ */
  var _trapHandler = null;
  var _lastCartFocus = null;

  function trapFocus(el) {
    releaseFocus();
    requestAnimationFrame(function () {
      var focusable = getFocusable(el);
      if (focusable.length) focusable[0].focus();
      _trapHandler = function (e) {
        if (e.key !== 'Tab') return;
        var f = getFocusable(el);
        if (!f.length) return;
        if (e.shiftKey) {
          if (document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
        } else {
          if (document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
        }
      };
      document.addEventListener('keydown', _trapHandler);
    });
  }

  function releaseFocus() {
    if (_trapHandler) { document.removeEventListener('keydown', _trapHandler); _trapHandler = null; }
  }

  function getFocusable(el) {
    return Array.from(el.querySelectorAll(
      'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])'
    )).filter(function (n) { return n.offsetParent !== null; });
  }

  /* ------------------------------------------------------------------ */
  /* Open / close                                                          */
  /* ------------------------------------------------------------------ */
  function openDrawer() {
    const d = drawer();
    if (!d) return;
    _lastCartFocus = document.activeElement;
    d.setAttribute('aria-hidden', 'false');
    d.classList.add('is-open');
    const ov = overlay();
    if (ov) { ov.classList.add('is-visible'); ov.setAttribute('aria-hidden', 'false'); }
    document.body.classList.add('gs-drawer-open');
    trapFocus(d);
  }

  function closeDrawer() {
    const d = drawer();
    if (!d) return;
    releaseFocus();
    d.setAttribute('aria-hidden', 'true');
    d.classList.remove('is-open');
    const ov = overlay();
    if (ov) { ov.classList.remove('is-visible'); ov.setAttribute('aria-hidden', 'true'); }
    document.body.classList.remove('gs-drawer-open');
    // Return focus to the cart icon (or whatever triggered the open)
    if (_lastCartFocus && _lastCartFocus.focus) {
      _lastCartFocus.focus();
    } else {
      const cartIcon = document.querySelector('.js-drawer-open-cart');
      if (cartIcon) cartIcon.focus();
    }
    _lastCartFocus = null;
  }

  /* ------------------------------------------------------------------ */
  /* Section Rendering API helpers                                         */
  /* ------------------------------------------------------------------ */
  function buildSectionsParam() {
    // Always request both sections so cart state is in sync
    let param = SECTION_ID;
    if (document.getElementById('GsCheckoutPopup') !== null ||
        document.querySelector('[data-section-id="' + POPUP_SECTION_ID + '"]') !== null) {
      param += ',' + POPUP_SECTION_ID;
    }
    return param;
  }

  function extractSectionHtml(responseJson, sectionId) {
    // /cart/add.js and /cart/change.js wrap rendered sections in a `sections`
    // object; the Section Rendering API (`/?sections=...`) returns them at the
    // TOP LEVEL of the JSON. Handle both — refreshCart() uses the latter, and
    // looking only under `sections` made every refresh() a silent no-op.
    if (responseJson.sections && responseJson.sections[sectionId]) {
      return responseJson.sections[sectionId];
    }
    if (typeof responseJson[sectionId] === 'string') {
      return responseJson[sectionId];
    }
    return null;
  }

  function syncPopupCartState(responseJson) {
    const popupHtml = extractSectionHtml(responseJson, POPUP_SECTION_ID);
    if (popupHtml && window.GsCheckoutPopup && typeof window.GsCheckoutPopup.refreshCartState === 'function') {
      window.GsCheckoutPopup.refreshCartState(popupHtml);
    }
  }

  function updateContents(sectionHtml) {
    if (!sectionHtml) return;
    const tmp = document.createElement('div');
    tmp.innerHTML = sectionHtml;
    const newContents = tmp.querySelector('#' + CONTENTS_ID);
    const current = contents();
    if (newContents && current) {
      current.replaceWith(newContents);
      // Re-bind delegated events on the new subtree
      bindContentEvents();
      updateCartCountBubbles(newContents);
      checkAndFireConfetti(newContents);
      handleRewardsAutoState();

      // Announce the cart change so other widgets (e.g. the bundle builder
      // box tracker) can re-sync from /cart.js. updateContents runs after
      // every drawer-side cart mutation, so this single hook covers qty
      // changes, removals, upsell adds, and rewards add/remove. Listeners
      // debounce, so render-only refreshes cost at most one extra fetch.
      document.dispatchEvent(new CustomEvent('gs:cart:changed'));
    }
  }

  function refreshCart() {
    const sectionsParam = buildSectionsParam();
    return fetch('/?sections=' + sectionsParam)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        const sectionHtml = extractSectionHtml(data, SECTION_ID);
        updateContents(sectionHtml);
        syncPopupCartState(data);
      })
      .catch(function (err) { console.error('[GsCartDrawer] refreshCart error:', err); });
  }

  /* ------------------------------------------------------------------ */
  /* Cart count bubbles (header icon badge)                               */
  /* ------------------------------------------------------------------ */
  function updateCartCountBubbles(contentsEl) {
    const el = contentsEl || contents();
    if (!el) return;
    const countEl = el.querySelector('#GsCartCount') || document.getElementById('GsCartCount');
    const itemCount = el.dataset ? parseInt(el.dataset.itemCount || '0', 10) : 0;
    // Update all Impulse header count badges
    document.querySelectorAll('[data-cart-count], .js-cart-count').forEach(function (badge) {
      badge.textContent = itemCount;
    });
    if (countEl) countEl.textContent = itemCount;
  }

  /* ------------------------------------------------------------------ */
  /* Capture-phase drawer-open interception                               */
  /* Blocks Impulse's native drawer from opening on cart-icon clicks.     */
  /* ------------------------------------------------------------------ */
  document.addEventListener('click', function (e) {
    // Cart icon click
    if (e.target.closest('.js-drawer-open-cart, [data-gs-cart-open]')) {
      e.preventDefault();
      e.stopPropagation();
      openDrawer();
      return;
    }
    // Close button inside our drawer
    if (e.target.closest('.gs-cart-drawer__close')) {
      closeDrawer();
      return;
    }
    // Overlay click — close
    if (e.target.id === OVERLAY_ID) {
      closeDrawer();
      return;
    }
  }, true); // capture phase

  /* ------------------------------------------------------------------ */
  /* Prevent native ajaxProduct:added from opening Impulse's drawer       */
  /* ------------------------------------------------------------------ */
  document.addEventListener('ajaxProduct:added', function (e) {
    e.stopImmediatePropagation();
    // We open our drawer instead — handled by handleFormSubmit already,
    // but this guard catches any other add-to-cart path.
  }, true);

  /* ------------------------------------------------------------------ */
  /* Capture-phase form submit interception (add-to-cart)                 */
  /* ------------------------------------------------------------------ */
  document.addEventListener('submit', handleFormSubmit, true);

  function handleFormSubmit(e) {
    const form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    const action = (form.getAttribute('action') || '').split('?')[0];
    if (!action.endsWith('/cart/add')) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    const formData = new FormData(form);
    const sectionsParam = buildSectionsParam();

    // Convert FormData to URLSearchParams so we can append sections
    const params = new URLSearchParams();
    for (const [key, val] of formData.entries()) {
      params.append(key, val);
    }
    params.append('sections', sectionsParam);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString()
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        const sectionHtml = extractSectionHtml(data, SECTION_ID);
        if (sectionHtml) {
          updateContents(sectionHtml);
        } else {
          // Fallback — re-fetch if sections weren't in the response
          return refreshCart();
        }
        syncPopupCartState(data);
        openDrawer();
        announce('Item added to cart.');
      })
      .catch(function (err) {
        console.error('[GsCartDrawer] add-to-cart error:', err);
      });
  }

  /* ------------------------------------------------------------------ */
  /* Delegated events inside #GsCartDrawerContents                        */
  /* ------------------------------------------------------------------ */
  function bindContentEvents() {
    const wrap = document.getElementById(DRAWER_ID);
    if (!wrap) return;
    // Re-binding is fine — we use event delegation on the static drawer root
  }

  document.addEventListener('click', function (e) {
    const d = drawer();
    if (!d) return;

    // Remove line item
    const removeBtn = e.target.closest('[data-action="remove-item"]');
    if (removeBtn && d.contains(removeBtn)) {
      e.preventDefault();
      const key = removeBtn.dataset.key;
      if (key) changeCartItem(key, 0);
      return;
    }

    // Quantity increment / decrement
    const qtyBtn = e.target.closest('[data-action="increment"], [data-action="decrement"]');
    if (qtyBtn && d.contains(qtyBtn)) {
      e.preventDefault();
      const key = qtyBtn.dataset.key;
      const newQty = parseInt(qtyBtn.dataset.qty, 10);
      if (key && !isNaN(newQty) && newQty >= 0) changeCartItem(key, newQty);
      return;
    }

    // Upsell add
    const upsellBtn = e.target.closest('[data-action="upsell-add"]');
    if (upsellBtn && d.contains(upsellBtn)) {
      e.preventDefault();
      const variantId = upsellBtn.dataset.variantId
        || upsellBtn.closest('.gs-upsell-card').querySelector('[data-gs-upsell-variant-select]')?.value
        || upsellBtn.closest('.gs-upsell-card').querySelector('.gs-upsell-card__variant-btn--selected')?.dataset.variantId;
      if (variantId) addToCart(parseInt(variantId, 10), 1);
      return;
    }

    // Upsell variant button selection
    const variantBtn = e.target.closest('.gs-upsell-card__variant-btn');
    if (variantBtn && d.contains(variantBtn)) {
      const card = variantBtn.closest('.gs-upsell-card');
      card.querySelectorAll('.gs-upsell-card__variant-btn').forEach(function (b) {
        b.classList.remove('gs-upsell-card__variant-btn--selected');
      });
      variantBtn.classList.add('gs-upsell-card__variant-btn--selected');
      const addBtn = card.querySelector('[data-action="upsell-add"]');
      if (addBtn) addBtn.dataset.variantId = variantBtn.dataset.variantId;
      return;
    }

    // Rewards toggle (checkbox or slider)
    const rewardsToggle = e.target.closest('#GsRewardsToggle');
    if (rewardsToggle && d.contains(rewardsToggle)) {
      handleRewardsToggle(rewardsToggle.checked);
      return;
    }

    // Rewards add button (UI style: none, opted-out)
    const rewardsAddBtn = e.target.closest('[data-action="rewards-add"]');
    if (rewardsAddBtn && d.contains(rewardsAddBtn)) {
      e.preventDefault();
      handleRewardsToggle(true);
      return;
    }

    // Rewards remove link (UI style: none, opted-in)
    const rewardsWithoutBtn = e.target.closest('[data-action="rewards-without"]');
    if (rewardsWithoutBtn && d.contains(rewardsWithoutBtn)) {
      e.preventDefault();
      handleRewardsToggle(false);
      return;
    }
  });

  /* upsell variant dropdown change */
  document.addEventListener('change', function (e) {
    if (!e.target.closest || !e.target.hasAttribute('data-gs-upsell-variant-select')) return;
    const d = drawer();
    if (!d || !d.contains(e.target)) return;
    const card = e.target.closest('.gs-upsell-card');
    const addBtn = card && card.querySelector('[data-action="upsell-add"]');
    if (addBtn) addBtn.dataset.variantId = e.target.value;
  });

  /* ------------------------------------------------------------------ */
  /* Cart mutations                                                        */
  /* ------------------------------------------------------------------ */
  function changeCartItem(key, qty) {
    const sectionsParam = buildSectionsParam();
    setLoading(true);
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty, sections: sectionsParam })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        const sectionHtml = extractSectionHtml(data, SECTION_ID);
        updateContents(sectionHtml);
        syncPopupCartState(data);
      })
      .catch(function (err) { console.error('[GsCartDrawer] changeCartItem error:', err); })
      .finally(function () { setLoading(false); });
  }

  function addToCart(variantId, qty) {
    const sectionsParam = buildSectionsParam();
    setLoading(true);
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty, sections: sectionsParam })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        const sectionHtml = extractSectionHtml(data, SECTION_ID);
        if (sectionHtml) {
          updateContents(sectionHtml);
        } else {
          return refreshCart();
        }
        syncPopupCartState(data);
      })
      .catch(function (err) { console.error('[GsCartDrawer] addToCart error:', err); })
      .finally(function () { setLoading(false); });
  }

  /* ------------------------------------------------------------------ */
  /* Loading state                                                         */
  /* ------------------------------------------------------------------ */
  function setLoading(on) {
    const d = drawer();
    if (d) d.classList.toggle('is-loading', on);
  }

  /* ------------------------------------------------------------------ */
  /* Live-region announcement                                             */
  /* ------------------------------------------------------------------ */
  function announce(msg) {
    const el = document.getElementById(ANNOUNCE_ID);
    if (el) { el.textContent = ''; requestAnimationFrame(function () { el.textContent = msg; }); }
  }

  /* ------------------------------------------------------------------ */
  /* Rewards auto-state on drawer open                                    */
  /* ------------------------------------------------------------------ */
  function handleRewardsAutoState() {
    const d = drawer();
    if (!d) return;
    const enabled = d.dataset.rewardsEnabled === 'true';
    if (!enabled) return;
    const defaultState = d.dataset.rewardsDefault;
    const contentsEl = contents();
    if (!contentsEl) return;
    const inCart = contentsEl.dataset.rewardsInCart === 'true';
    const variantId = parseInt(d.dataset.rewardsVariant, 10);
    if (!variantId) return;

    if (defaultState === 'opted-in' && !inCart) {
      const alreadyDismissed = sessionStorage.getItem('gs_rewards_default_applied');
      if (!alreadyDismissed) {
        addRewardsToCart(variantId);
        sessionStorage.setItem('gs_rewards_default_applied', '1');
      }
    }
  }

  function handleRewardsToggle(shouldAdd) {
    const d = drawer();
    if (!d) return;
    const variantId = parseInt(d.dataset.rewardsVariant, 10);
    const contentsEl = contents();
    if (!variantId || !contentsEl) return;
    const lineKey = contentsEl.dataset.rewardsLineKey || '';

    if (shouldAdd) {
      addRewardsToCart(variantId);
    } else {
      if (lineKey) {
        changeCartItem(lineKey, 0);
      }
    }
  }

  function addRewardsToCart(variantId) {
    const sectionsParam = buildSectionsParam();
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1, sections: sectionsParam })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        const sectionHtml = extractSectionHtml(data, SECTION_ID);
        if (sectionHtml) updateContents(sectionHtml);
        syncPopupCartState(data);
      })
      .catch(function (err) { console.error('[GsCartDrawer] addRewards error:', err); });
  }

  /* ------------------------------------------------------------------ */
  /* Confetti                                                             */
  /* ------------------------------------------------------------------ */
  let confettiLib = null;

  function loadConfetti() {
    return new Promise(function (resolve, reject) {
      if (confettiLib) { resolve(confettiLib); return; }
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.3/dist/confetti.browser.min.js';
      s.onload = function () { confettiLib = window.confetti; resolve(confettiLib); };
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  function fireConfetti(tier) {
    const canvas = document.getElementById('GsConfettiCanvas');
    if (!canvas) return;
    loadConfetti().then(function (confetti) {
      const myConfetti = confetti.create(canvas, { resize: true, useWorker: false });
      const opts = tier === 2
        ? { particleCount: 80, spread: 90, startVelocity: 30, colors: ['#111111', '#ffffff', '#e8e8e1'] }
        : { particleCount: 60, spread: 70, startVelocity: 25, colors: ['#111111', '#ffffff', '#e8e8e1'] };
      myConfetti(Object.assign({ origin: { y: 0.5 } }, opts));
    }).catch(function () { /* confetti is a nice-to-have */ });
  }

  function checkAndFireConfetti(contentsEl) {
    if (!contentsEl) return;
    const bars = contentsEl.querySelectorAll('[data-progress-tier]');
    bars.forEach(function (bar) {
      const tier = bar.dataset.progressTier;
      const pct = parseInt(bar.dataset.progressPct, 10);
      const key = 'gs_confetti_tier' + tier + '_shown';
      if (pct >= 100 && !sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1');
        fireConfetti(parseInt(tier, 10));
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* ESC key                                                               */
  /* ------------------------------------------------------------------ */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeDrawer();
  });

  /* ------------------------------------------------------------------ */
  /* Public API                                                            */
  /* ------------------------------------------------------------------ */
  window.GsCartDrawer = {
    open: openDrawer,
    close: closeDrawer,
    refresh: refreshCart
  };

})();
